type CorpoDaApi<T = { mensagem?: string }> = {
  success?: boolean;
  data?: T | null;
  notifications?: string[];
  message?: string;
};

type ErroDoAxios = {
  response?: { status?: number; data?: unknown };
  request?: unknown;
  isAxiosError?: boolean;
  code?: string;
  message?: string;
};

export const MENSAGEM_SEM_CONEXAO =
  'Não foi possível falar com o servidor. Verifique a conexão e tente novamente.';

export const MENSAGEM_SEM_PERMISSAO =
  'Sua sessão expirou ou você não tem permissão para esta ação.';

/**
 * O interceptor não devolve sempre a mesma coisa: no sucesso vem a resposta do
 * axios, e a partir do 400 ele resolve com o corpo da resposta já desembrulhado
 * (`{ success: false, data: null, notifications }`). Quem lê `resposta.data.success`
 * direto quebra no segundo caso, porque `data` é nulo. Este helper devolve
 * sempre o envelope, ou undefined quando não veio corpo nenhum (401, 500).
 */
export function corpoDaApi<T = { mensagem?: string }>(
  resposta: unknown
): CorpoDaApi<T> | undefined {
  const axios = resposta as { data?: CorpoDaApi<T> } | undefined;

  if (axios?.data && typeof axios.data === 'object' && 'success' in axios.data) {
    return axios.data;
  }

  const envelope = resposta as CorpoDaApi<T> | undefined;

  return envelope && typeof envelope === 'object' && 'success' in envelope ? envelope : undefined;
}

/**
 * true quando o interceptor já mostrou o motivo em toast, para o chamador não
 * empilhar um "ocorreu algo inesperado" genérico por cima. Aceita tanto o erro
 * lançado quanto o envelope resolvido, porque o interceptor entrega os dois.
 */
export function foiAvisado(alvo: unknown): boolean {
  const erro = alvo as ErroDoAxios | undefined;

  // Erro rejeitado: o interceptor só avisa no 401 ("Acesso não autorizado").
  // 409 e 5xx rejeitam calados, então quem chamou é que precisa falar.
  if (erro?.response) return erro.response.status === 401;

  const envelope = alvo as CorpoDaApi<unknown> | undefined;

  return Array.isArray(envelope?.notifications) && envelope!.notifications!.length > 0;
}

function textoValido(valor: unknown): valor is string {
  return typeof valor === 'string' && valor.trim().length > 0;
}

function primeiraNotificacao(corpo: unknown): string | undefined {
  const envelope = corpo as CorpoDaApi<unknown> | undefined;
  const primeira = Array.isArray(envelope?.notifications) ? envelope!.notifications![0] : undefined;

  return textoValido(primeira) ? primeira : undefined;
}

function mensagemDoCorpo(corpo: unknown): string | undefined {
  const envelope = corpo as CorpoDaApi<unknown> | undefined;

  return textoValido(envelope?.message) ? envelope!.message : undefined;
}

/**
 * Texto para quem usa o painel, a partir do que a API devolveu: envelope
 * resolvido (400/402), erro rejeitado com corpo (409, 5xx), 401/403 sem corpo
 * ou falha de rede. Sem isso toda falha virava "ocorreu algo inesperado".
 */
export function mensagemDaFalha(alvo: unknown, padrao: string): string {
  if (!alvo || typeof alvo !== 'object') return padrao;

  const erro = alvo as ErroDoAxios;

  if (erro.response) {
    const { status = 0, data } = erro.response;
    const corpo = data && typeof data === 'object' ? data : undefined;

    // No 5xx a notificação é a exceção crua do servidor; só a message serve à tela.
    if (status >= 500) return mensagemDoCorpo(corpo) ?? padrao;

    const motivo = primeiraNotificacao(corpo) ?? mensagemDoCorpo(corpo);

    if (motivo) return motivo;

    return status === 401 || status === 403 ? MENSAGEM_SEM_PERMISSAO : padrao;
  }

  const envelope = corpoDaApi(alvo);

  if (envelope) return primeiraNotificacao(envelope) ?? mensagemDoCorpo(envelope) ?? padrao;

  const semResposta =
    erro.isAxiosError === true ||
    erro.request !== undefined ||
    erro.code === 'ECONNABORTED' ||
    erro.message === 'Network Error';

  return semResposta ? MENSAGEM_SEM_CONEXAO : padrao;
}

/**
 * Falha que já vem com um texto escrito para quem usa o painel. Só uma dessas
 * chega à tela do widget; qualquer outro erro cai no texto padrão do quadro.
 */
export class ErroDoWidget extends Error {
  constructor(motivo: string) {
    super(motivo);
    this.name = 'ErroDoWidget';
  }
}

export const MENSAGEM_FALHA_NA_ANALISE = 'Falha ao gerar a análise.';

export const MENSAGEM_ANALISE_INDISPONIVEL = 'A análise com IA não está disponível no momento.';

export type MotivoDaFalhaDaAnalise = 'falha' | 'indisponivel' | 'sessao';

/**
 * Falha da análise com IA, já com o texto para o modal. O motivo diz se o
 * recurso não existe nesta instalação (412) ou se a sessão acabou no meio e a
 * página vai para o login; o resto é falha comum, com "Tentar novamente".
 */
export class ErroDaAnaliseIa extends Error {
  constructor(mensagem: string, readonly motivo: MotivoDaFalhaDaAnalise = 'falha') {
    super(mensagem);
    this.name = 'ErroDaAnaliseIa';
  }
}
